import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Spacer } from '@tink/core/src/components/atoms/Spacer/Spacer';
import { ProviderField } from '../../../../../../../models/provider';
import { ContentBody } from '../../../../../../atoms/ContentBody/ContentBody';
import { Button } from '../../../../../../atoms/Button/Button';
import { Footer } from '../../../../../../atoms/Footer/Footer';
import {
  ProviderInputs,
  SubmitProviderInputsPayload,
} from '../../../../../SubmitCredentials/molecules/ProviderInputs/ProviderInputs';
import { TemplateElementType, Templates } from '../../../../../../Templates/templates';
import { PageView, UserInteractionEvent } from '../../../../../../../models/analytics';
import { MaybeChangeMethodButton } from '../../../../../../Templates/MaybeChangeMethodButton/MaybeChangeMethodButton';
import { TitleImageFields } from '../../TitleImageFields';
import { usePageViewEvents } from '../../../../../../../core/Analytics/usePageViewEvents';
import { H1 } from '../../../../../../atoms/Heading/Heading';
import { Text } from '../../../../../../atoms/Text/Text';
import { commonMessages } from '../../../../../../atoms/CommonMessages/messages';
import { messages } from '../../../../../../Templates/VerificationTemplate/messages';

export type ImageCodeViewProps = {
  fields: ProviderField[];
  onSubmit: (values: SubmitProviderInputsPayload) => void;
};

export const ImageCodeView = ({ fields, onSubmit }: ImageCodeViewProps) => {
  const { sendViewInteractionEvent } = usePageViewEvents({
    pageView: PageView.IMAGE_CODE_TEMPLATE_SCREEN,
  });

  const handleSubmitWithAnalytics = useCallback(
    (values: SubmitProviderInputsPayload) => {
      sendViewInteractionEvent({
        interaction: UserInteractionEvent.SUBMIT_SUPPLEMENTAL_INFORMATION,
      });
      onSubmit(values);
    },
    [onSubmit, sendViewInteractionEvent]
  );

  const [isButtonDisabled, setIsButtonDisabled] = useState(true);

  const parsedElements = useMemo(() => {
    const title = fields.find((el) => el.type === TemplateElementType.TEXT);
    const image = fields.find((el) => el.type === TemplateElementType.IMAGE);
    const input = fields.filter((el) => el.type === TemplateElementType.INPUT);

    return { title, image, input };
  }, [fields]);

  const { title, image, input } = parsedElements;

  return (
    <ContentBody data-testid="image-code-template">
      <H1 mb="xxsmall">{title?.value ? title.value : <FormattedMessage {...messages.title} />}</H1>

      <Text color="secondary" mb="small">
        {title?.helpText ?? <FormattedMessage {...messages.subtitle} />}
      </Text>
      <Spacer size="xsmall" />

      <TitleImageFields image={image} />

      <ProviderInputs
        formId={Templates.IMAGE_CODE}
        providerFields={input}
        setButtonDisabled={(newState: boolean) => setIsButtonDisabled(newState)}
        onSubmit={handleSubmitWithAnalytics}
      >
        <Spacer size="normal" />

        <Footer stickToBottom>
          <Button
            form={Templates.IMAGE_CODE}
            disabled={isButtonDisabled}
            type="submit"
            color="primary"
            variant="contained"
          >
            <FormattedMessage {...commonMessages.continue} />
          </Button>
          <MaybeChangeMethodButton elements={fields} onSubmit={onSubmit} />
        </Footer>
      </ProviderInputs>
    </ContentBody>
  );
};
